
import { useEffect, useState } from 'react';
import { notification, message } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import OnlineTest from '../ExamComponent/MCQuestions';
import TabChangeDetection from './CheatingDetection/TabChangeDetection';
import { getData } from '../../../actions/apiCall';
import { decryptData } from '../../../actions/crypto';
import CodeEditorComponent from '../ExamComponent/CodingTest';
import AIInterview from '../ExamComponent/AIInterview';
import { examConfig } from '../../../config/examConfig';

const ProtectedComponent = ({ }) => {
    const { id } = useParams();
    const location = useLocation();
    const [testDetails, setTestDetails] = useState<any>();
    const [questionType, setQuestionType] = useState<any>(null); // Don't default to 'mcq'

    const dispatch = useDispatch()

    useEffect(() => {
        if (id) {
            getTestDetails()
        }
    }, [id]);

    // Global Security Measures: Apply to all exam rounds (MCQ, AI Interview, Coding)
    // Only apply when on test/exam pages
    useEffect(() => {
        // Check if we're on a test/exam page
        const isTestPage = location.pathname.includes('/online-exam') || location.pathname.includes('/exam');

        if (!isTestPage) {
            return; // Don't apply security restrictions if not on test pages
        }

        // Disable text selection
        const disableSelection = (e: Event) => {
            e.preventDefault();
            return false;
        };

        // Disable copy, cut, paste
        const disableCopyPaste = (e: ClipboardEvent) => {
            e.preventDefault();
            message.warning('Copy/Paste is disabled during the exam');
            return false;
        };

        // Disable keyboard shortcuts
        const disableShortcuts = (e: KeyboardEvent) => {
            // Disable Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A, Ctrl+P, Ctrl+S, Ctrl+F, Ctrl+H, Ctrl+U, etc.
            if (e.ctrlKey || e.metaKey) {
                const key = e.key.toLowerCase();
                // Block common shortcuts: copy, paste, cut, select all, print, save, find, history, view source, etc.
                if (['c', 'v', 'x', 'a', 'p', 's', 'f', 'h', 'u', 'i', 'j', 'k', 'w', 'n', 't', 'r', 'l', 'o', 'g', 'd', 'b', 'e', 'y', 'z'].includes(key)) {
                    e.preventDefault();
                    e.stopPropagation();
                    message.warning('This shortcut is disabled during the exam');
                    return false;
                }
            }

            // Disable F12 (Developer Tools), Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C, Ctrl+Shift+K
            if (examConfig.enableCheatingDetection && (e.key === 'F12' ||
                (e.ctrlKey && e.shiftKey && ['i', 'j', 'c', 'k'].includes(e.key.toLowerCase())))) {
                e.preventDefault();
                e.stopPropagation();
                message.error('Developer tools are disabled during the exam');
                return false;
            }

            // Disable Print Screen and screenshot shortcuts
            if (e.key === 'PrintScreen' ||
                (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'p') ||
                (e.key === 'F13')) {
                e.preventDefault();
                e.stopPropagation();
                message.warning('Screenshots are disabled during the exam');
                return false;
            }

            // Disable right-click context menu shortcuts
            if (e.key === 'ContextMenu') {
                e.preventDefault();
                return false;
            }

            // Disable Alt+Tab (Alt key combinations)
            if (e.altKey && ['tab', 'f4'].includes(e.key.toLowerCase())) {
                e.preventDefault();
                e.stopPropagation();
                message.warning('Tab switching is disabled during the exam');
                return false;
            }

            return true;
        };

        // Disable right-click context menu
        const disableContextMenu = (e: MouseEvent) => {
            e.preventDefault();
            message.warning('Right-click is disabled during the exam');
            return false;
        };

        // Disable drag and drop
        const disableDragDrop = (e: DragEvent) => {
            e.preventDefault();
            return false;
        };

        // Add event listeners
        document.addEventListener('selectstart', disableSelection);
        document.addEventListener('copy', disableCopyPaste);
        document.addEventListener('cut', disableCopyPaste);
        document.addEventListener('paste', disableCopyPaste);
        document.addEventListener('keydown', disableShortcuts);
        document.addEventListener('keyup', disableShortcuts);
        document.addEventListener('contextmenu', disableContextMenu);
        document.addEventListener('dragstart', disableDragDrop);
        document.addEventListener('drop', disableDragDrop);

        // Disable text selection via CSS - scoped to test pages only
        // Add class to body to scope styles (more compatible than :has())
        document.body.classList.add('exam-page-active');

        const style = document.createElement('style');
        style.id = 'exam-security-styles';
        style.textContent = `
            /* Only apply to elements when body has exam-page-active class */
            body.exam-page-active * {
                -webkit-user-select: none !important;
                -moz-user-select: none !important;
                -ms-user-select: none !important;
                user-select: none !important;
                -webkit-touch-callout: none !important;
                -webkit-tap-highlight-color: transparent !important;
            }
            /* Allow selection in code editor and input fields for functionality, but copy/paste is still blocked via JS */
            body.exam-page-active .monaco-editor,
            body.exam-page-active .monaco-editor *,
            body.exam-page-active input[type="text"],
            body.exam-page-active input[type="number"],
            body.exam-page-active input[type="email"],
            body.exam-page-active input[type="password"],
            body.exam-page-active textarea,
            body.exam-page-active [contenteditable="true"] {
                -webkit-user-select: text !important;
                -moz-user-select: text !important;
                -ms-user-select: text !important;
                user-select: text !important;
            }
            body.exam-page-active img {
                -webkit-user-drag: none !important;
                -moz-user-drag: none !important;
                user-drag: none !important;
            }
        `;
        document.head.appendChild(style);

        // Disable image dragging
        const images = document.querySelectorAll('img');
        images.forEach(img => {
            img.setAttribute('draggable', 'false');
        });

        // Cleanup
        return () => {
            document.removeEventListener('selectstart', disableSelection);
            document.removeEventListener('copy', disableCopyPaste);
            document.removeEventListener('cut', disableCopyPaste);
            document.removeEventListener('paste', disableCopyPaste);
            document.removeEventListener('keydown', disableShortcuts);
            document.removeEventListener('keyup', disableShortcuts);
            document.removeEventListener('contextmenu', disableContextMenu);
            document.removeEventListener('dragstart', disableDragDrop);
            document.removeEventListener('drop', disableDragDrop);
            // Remove global security styles
            const securityStyle = document.getElementById('exam-security-styles');
            if (securityStyle) {
                securityStyle.remove();
            }
            // Remove class from body
            document.body.classList.remove('exam-page-active');
        };
    }, [location.pathname]); // Re-run when pathname changes

    // Detect browser extensions and DevTools (basic detection)
    useEffect(() => {
        if (!examConfig.enableCheatingDetection) {
            return; // Skip devtools detection if cheating detection is disabled
        }

        // Check for common extension indicators
        const extensionIndicators = [
            (window as any).chrome?.runtime?.id, // Chrome extensions
            (window as any).browser?.runtime?.id, // Firefox extensions
        ];

        // Check if DevTools is open (basic detection)
        let devtools = false;
        const threshold = 160;
        const devtoolsCheckInterval = setInterval(() => {
            if (window.outerHeight - window.innerHeight > threshold ||
                window.outerWidth - window.innerWidth > threshold) {
                if (!devtools) {
                    devtools = true;
                    message.error('Developer tools detected! Please close them to continue.');
                }
            } else {
                devtools = false;
            }
        }, 1000);

        return () => {
            clearInterval(devtoolsCheckInterval);
        };
    }, []);

    // Prevent tab/window switching (enhanced)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                message.error('Tab switching detected! Please stay on this tab.');
            }
        };

        const handleBlur = () => {
            message.warning('Window lost focus. Please stay focused on the exam.');
        };

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = 'Are you sure you want to leave? Your progress may be lost.';
            return e.returnValue;
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const getTestDetails = async () => {
        try {
            const resData = await dispatch(getData(`/accel-test/${id}`));
            if (resData.code === 200) {
                const data = decryptData(resData.data);

                if (data?.testStatus === 'completed') {
                    notification.error({ message: 'Test Already Completed' });
                    window.location.href = `/instructions/${id}`; // Redirect to Error page
                    return;
                }

                if (data?.testStatus === 'pending') {
                    notification.error({ message: 'Test Not Started' });
                    window.location.href = `/instructions/${id}`; // Redirect to Error page
                    return;
                }

                if (data?.testStatus === 'expired') {
                    notification.error({ message: 'Test Expired' });
                    window.location.href = `/instructions/${id}`; // Redirect to Error page
                    return;
                }

                if (data?.testStatus === 'cancelled') {
                    notification.error({ message: 'Test Cancelled' });
                    window.location.href = `/instructions/${id}`; // Redirect to Error page
                    return;
                }

                setTestDetails(data);

                const isAIInterview = data?.testType === 'ai-interview' ||
                    data?.aiInterview === true ||
                    (data?.totalMCQQuestions === 0 && data?.totalCodingQuestions === 0 &&
                        (data?.aiInterviewConfig?.length > 0 || data?.totalAIInterviewQuestions > 0));

                if (data?.testStage === 'coding') {
                    setQuestionType('coding');
                } else if (data?.testStage === 'mcq') {
                    if (!isAIInterview) {
                        setQuestionType('mcq');
                    } else {
                        setQuestionType('ai-interview');
                    }
                } else if (data?.testStage === 'ai-interview' || isAIInterview) {
                    setQuestionType('ai-interview');
                } else {
                    if (isAIInterview) {
                        setQuestionType('ai-interview');
                    } else if (data?.mcqQuestions?.length > 0 && !isAIInterview) {
                        setQuestionType('mcq');
                    } else if (data?.coddingQuestions?.length > 0) {
                        setQuestionType('coding');
                    } else {
                        setQuestionType('ai-interview');
                    }
                }
            } else {
                window.location.href = `/instructions/${id}`; // Redirect to Error page
                return;
            }
        } catch (error) {
            console.log(error);
            window.location.href = `/instructions/${id}`; // Redirect to Error page
            return;
        }
    }

    const handleAIInterviewComplete = () => {
        // Reload test details to get updated testStage from backend
        getTestDetails();
    };

    // Don't render anything until we've determined the question type
    if (!questionType || !testDetails) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>Loading test...</div>
            </div>
        );
    }

    // Determine if this is an AI interview test to prevent MCQ from rendering
    const isAIInterviewTest = testDetails?.testType === 'ai-interview' ||
        testDetails?.aiInterview === true ||
        (testDetails?.totalMCQQuestions === 0 && testDetails?.totalCodingQuestions === 0 &&
            (testDetails?.aiInterviewConfig?.length > 0 || testDetails?.totalAIInterviewQuestions > 0));

    const examContent = (
        <>
            {questionType == 'ai-interview' && <AIInterview testDetails={testDetails} onSubmit={handleAIInterviewComplete} />}
            {questionType == 'mcq' && !isAIInterviewTest && <OnlineTest testDetails={testDetails} onSubmitMCQ={() => setQuestionType('coding')} />}
            {questionType == 'coding' && <CodeEditorComponent />}
        </>
    );

    if (examConfig.enableCheatingDetection) {
        return (
            <TabChangeDetection>
                {examContent}
            </TabChangeDetection>
        );
    }

    return examContent;
};

export default ProtectedComponent;
